import { debounce } from '@ember/runloop';
import { service } from '@ember/service';

import type Highcharts from 'highcharts';

import type { DChartInterface, Service } from 'webapp-types';

import type VisualizationBubble from 'webapp/models-custom/visualization/bubble';
import BaseRenderer from 'webapp/d-chart/renderer/base';

// a series as built here; its type comes from the chart options
type BubbleSeriesOptions = Omit<
	Highcharts.SeriesPackedbubbleOptions,
	'type' | 'data' | 'colorIndex'
> & {
	data: DChartInterface.ChartDataPoint[];
	colorIndex?: string | number; // a class for custom colors
};

export default class BubbleRenderer extends BaseRenderer {
	@service('navigation') declare navigationService: Service.Navigation;

	override get visualization(): VisualizationBubble {
		return super.visualization as VisualizationBubble;
	}

	get series() {
		return this.visualization.series;
	}

	get hasLegend() {
		return !this.visualization.isLegendOff;
	}

	get showValues() {
		return this.visualization.showValues;
	}

	override get baseOptions(): Highcharts.Options {
		const that = this;

		const isAnimated = this.dChart.isAnimated;

		let options = super.baseOptions;

		options = this.mergeOptions(options, {
			chart: {
				type: 'packedbubble',
				height: '100%',
				events: {
					render: function (this: Highcharts.Chart, _event: Event) {
						if (that.hasLegend) {
							debounce(that, that.centerLegendItems, this.legend, 500);
						}
					},
				},
			},
			plotOptions: {
				series: {
					events: {
						click: function (
							this: Highcharts.Series,
							event: Highcharts.SeriesClickEventObject,
						) {
							that.onPointClick(
								(event.point as DChartInterface.ChartHoverPoint).dbxPoint,
								event,
								(event.point as DChartInterface.ChartHoverPoint).graphic!.element,
							);
						},
					},
				},
				packedbubble: {
					minSize: '20%',
					maxSize: '95%',
					draggable: !this.navigationService.isDesigner,
					animation: isAnimated,
					layoutAlgorithm: {
						enableSimulation: isAnimated,
					},
					dataLabels: {
						formatter: function (
							this: Highcharts.Point & { point?: DChartInterface.ChartHoverPoint },
						) {
							if (that.visualization.showValuesAsLabels) {
								return this.point!.dbxPoint.valueStr;
							} else {
								return that.sanitize(this.point!.dbxPoint.title);
							}
						},
					},
				},
			},
		});

		return options;
	}

	override get dataOptions(): Highcharts.Options {
		const options: Omit<Highcharts.Options, 'series'> & { series: BubbleSeriesOptions[] } = {
			series: [],
			legend: {
				enabled: this.hasLegend,
			},
		};

		const showValues = this.showValues;

		this.series.forEach((series) => {
			const seriesClass = series.points[0]?.onClick ? 'series-clickable' : '';

			const seriesData: BubbleSeriesOptions = {
				className: seriesClass,
				dataLabels: {
					enabled: showValues,
				},
				colorIndex: series.colorIndexOrClass,
				name: series.title,
				data: [],
			};

			series.points.forEach((point) => {
				const pointData: DChartInterface.ChartDataPoint = {
					name: point.title,
					value: point.value, // different from others - "value" instead of "y"
					dbxPoint: point,
				};
				if (point.value && point.value < 0) {
					pointData.className = 'negative';
				}
				seriesData.data.push(pointData);
			});

			options.series.push(seriesData);
		});

		return options;
	}

	override get sizeOptions(): Highcharts.Options {
		let options = super.sizeOptions;

		options = this.mergeOptions(options, {
			legend: this.defaultLegendOptions,
		});

		return options;
	}

	override updateSize(_hasModifierChanged: boolean): void {
		// update also when modifier is unchanged, only size changed bcos od defaultLegendOptions
		super.updateSize(true);
	}

	override updateTheme(): void {
		this.visualization.updateColorIndexes();
		super.updateTheme();
	}
}
